/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { SetterOrUpdater } from "recoil";
import { ContactApi, IUpdateContactResponse } from "../../../../api/Contacts";
import { ContactFilter } from "../../../../atoms/ContactFilterAtom";
import { IContactFiltersAtom } from "../../../../atoms/ContactFiltersAtom";
import { IContactTag } from "../../../../generated/Api";
import { IContact } from "../../../../types/Contact";
import { ITag } from "../../../../types/TagTypes";
import LoadingAbsolute from "../../../LoadingAbsolute";
import StyledButton from "../../../StyledButton";
import TagSelect from "../../../TagSelect/TagSelect";
import { useModal } from "../../Modal";

const TagsModalContainer = styled.div`
  width: 400px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100%;
  }
`;

const Title = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 600;
  color: #2f2f2f;
`;

const TagsContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 10px;

  > div {
    width: 100%;
  }
`;

const ModalFooter = styled.div`
  width: calc(100% + 50px);

  padding: 25px 25px 0 25px;

  border-top: 1px solid;
  border-color: inherit;

  display: flex;
  align-items: center;
  justify-content: end;
  grid-gap: 12px;

  @media screen and (max-width: 1024px) {
    width: 100%;

    padding: 25px;
  }
`;

const Container = styled.div`
  width: calc(100% + 50px);

  margin-top: 25px;
  padding: 25px;

  border-top: 1px solid;
  border-color: inherit;

  @media screen and (max-width: 1024px) {
    width: 100%;
    height: 100%;

    margin-top: 0;
    padding: 25px;

    border-top: none;
  }
`;

type Props = {
  setContacts?: SetterOrUpdater<IContact[]>;
} & (
  | {
      selectedContactIds: string[];
    }
  | {
      selectAll: true;
      contactFilter?: ContactFilter | null;
      query: IContactFiltersAtom;
    }
);

export default function BulkAddAndRemoveTagModal(props: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [addSelectedTags, setAddSelectedTags] = useState<ITag[]>([]);
  const [removeSelectedTags, setRemoveSelectedTags] = useState<ITag[]>([]);

  const modal = useModal();

  async function handleClick() {
    setIsLoading(true);
    const addTagIds = addSelectedTags.map(({ _id }) => _id!);
    const removeTagIds = removeSelectedTags.map(({ _id }) => _id!);

    let response: IUpdateContactResponse;

    if ("selectAll" in props) {
      let tagQuery = props.contactFilter?.tagQuery;
      response = await ContactApi.addAndRemoveManyTag(
        addTagIds,
        removeTagIds,
        undefined,
        {
          ...props.query,
          tagQuery,
        }
      );
    } else {
      response = await ContactApi.addAndRemoveManyTag(
        addTagIds,
        removeTagIds,
        props.selectedContactIds
      );
    }

    modal.popModal();

    if (!response) {
      toast("Error on add and remove tags on contacts, try again.", {
        type: "error",
      });
      setIsLoading(false);

      return;
    }

    if (addTagIds.length > 0 && !removeTagIds.length) {
      toast("Tags added successfully!", { type: "success" });
    } else if (removeTagIds.length > 0 && !addTagIds.length) {
      toast("Tags removed successfully!", { type: "success" });
    } else {
      toast("Tags added/removed successfully!", { type: "success" });
    }

    if (!props.setContacts) return;

    props.setContacts((prevContacts) => {
      if (!prevContacts) return;

      const array = prevContacts.map((prevContact, index) => {
        if (
          "selectedContactIds" in props &&
          !props.selectedContactIds.includes(prevContact._id)
        )
          return prevContact;

        let allContactTags = prevContact.tags.filter(
          (tag) => !removeTagIds.find((tagId) => tag._id === tagId)
        );

        allContactTags = [
          ...allContactTags,
          ...addSelectedTags
            .filter((at) => !allContactTags.find((t) => t._id === at._id))
            .map((t) => ({
              _id: t._id as string,
              appliedAt: new Date().toDateString(),
            })),
        ];

        let uniqueTagsMap: { [tagId: string]: IContactTag } = {};

        allContactTags.forEach((t) => (uniqueTagsMap[t._id] = t));

        return {
          ...prevContact,
          tags: Object.values(uniqueTagsMap),
        };
      });

      setIsLoading(false);
      return array;
    });
  }

  return (
    <TagsModalContainer>
      <Container>
        <TagsContainer>
          <Title>Apply Tags</Title>

          <div
            css={css`
              > div > div:nth-child(2) {
                max-height: 120px;

                margin-top: 16px;

                display: flex;
                align-items: center;
                justify-content: start;
                flex-wrap: wrap;
                grid-gap: 12px;

                overflow-y: scroll;
              }

              > div div:nth-child(2) div {
                grid-gap: 4px;

                margin: 0 !important;
              }
            `}
          >
            <TagSelect
              tags={addSelectedTags}
              preserveSystemTags={true}
              handleChoseTags={(tags) => setAddSelectedTags(tags)}
            />
          </div>
        </TagsContainer>

        <TagsContainer style={{ marginTop: "25px" }}>
          <Title>Remove Tags</Title>

          <div
            css={css`
              > div > div:nth-child(2) {
                max-height: 120px;

                margin-top: 16px;

                display: flex;
                align-items: center;
                justify-content: start;
                flex-wrap: wrap;
                grid-gap: 12px;

                overflow-y: scroll;
              }

              > div div:nth-child(2) div {
                grid-gap: 4px;

                margin: 0 !important;
              }
            `}
          >
            <TagSelect
              tags={removeSelectedTags}
              preserveSystemTags={true}
              handleChoseTags={(tags) => setRemoveSelectedTags(tags)}
            />
          </div>
        </TagsContainer>
      </Container>

      <ModalFooter>
        <StyledButton
          inverted
          css={css`
            width: 70px;
          `}
          onClick={modal.popModal}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 70px;
          `}
          isLoading={isLoading}
          onClick={handleClick}
        >
          Save
        </StyledButton>
      </ModalFooter>

      {isLoading && <LoadingAbsolute />}
    </TagsModalContainer>
  );
}
